// hooks/useDashboard.js - Fixed version with better error handling and debugging
import { useState, useCallback, useEffect, useRef } from 'react';
import { dataLoader } from '../dataLoader';
import {
    checkHealth,
    fetchTables,
    fetchAccessData,
    fetchTableStats,
    fetchFilterOptions
} from '../services/api/dashboardQueryApi';

export const useDashboard = () => {
    // State management
    const [accessData, setAccessData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [connectionStatus, setConnectionStatus] = useState('checking');

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [originFilter, setOriginFilter] = useState('All Origins');
    const [countryFilter, setCountryFilter] = useState('All Countries');

    // Data states
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState('access_entries');
    const [countries, setCountries] = useState([]);
    const [origins, setOrigins] = useState([]);
    const [stats, setStats] = useState({ total: 0, origins: 0 });
    const searchTimeoutRef = useRef(null);
    const fetchAbortRef = useRef(null);

    // Pagination state
    const [pagination, setPagination] = useState({
        hasMore: false,
        totalRecords: 0,
        currentOffset: 0,
        limit: 1000
    });

    // Fetch data with filters - improved version
    const fetchData = useCallback(async (
        table = selectedTable,
        search = searchTerm,
        origin = originFilter,
        country = countryFilter,
        offset = 0,
        append = false
    ) => {
        console.log('🔄 Fetching data with params:', { table, search, origin, country, offset, append });

        if (!append) {
            setLoading(true);
        }
        setError('');

        let controller = null;
        try {
            if (fetchAbortRef.current && !append) {
                fetchAbortRef.current.abort();
            }
            controller = new AbortController();
            if (!append) fetchAbortRef.current = controller;

            // Fetch access data
            const result = await fetchAccessData({
                selectedTable: table,
                searchTerm: search,
                originFilter: origin,
                countryFilter: country,
                limit: pagination.limit,
                offset: offset,
                signal: controller.signal
            });

            console.log('📊 Data received:', {
                records: result.data.length,
                total: result.totalRecords,
                hasMore: result.hasMore
            });

            if (append) {
                setAccessData(prev => [...prev, ...result.data]);
            } else {
                setAccessData(result.data);
            }

            setPagination({
                hasMore: result.hasMore,
                totalRecords: result.totalRecords,
                currentOffset: offset + result.data.length,
                limit: pagination.limit
            });

        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('❌ Error fetching data:', err);
            setError(`Failed to fetch data: ${err.message}`);
        } finally {
            if (!append && fetchAbortRef.current === controller) fetchAbortRef.current = null;
            setLoading(false);
        }
    }, [selectedTable, searchTerm, originFilter, countryFilter, pagination.limit]);

    // Initialize dashboard with improved error handling
    const initializeDashboard = useCallback(async () => {
        console.log('🚀 Initializing dashboard...');
        setLoading(true);
        setError('');

        try {
            console.log('📡 Checking backend connection...');
            const isHealthy = await checkHealth();
            console.log('📡 Backend health check result:', isHealthy);
            setConnectionStatus(isHealthy ? 'connected' : 'disconnected');

            if (!isHealthy) {
                throw new Error('Backend server is not responding. Please check if the server is running on port 3001.');
            }

            console.log('📁 Loading filter data from files...');
            const [loadedCountries, loadedOrigins] = await Promise.all([
                dataLoader.loadCountriesFromFile(),
                dataLoader.loadOriginsFromFile()
            ]);

            setCountries(loadedCountries);
            setOrigins(loadedOrigins);
            console.log('📁 Filter data loaded:', { countries: loadedCountries.length, origins: loadedOrigins.length });

            console.log('📊 Fetching available tables...');
            const tablesData = await fetchTables();
            setTables(tablesData);
            console.log('📊 Tables loaded:', tablesData);

            const firstTable = tablesData.length > 0 ? tablesData[0].table_name : 'access_entries';
            setSelectedTable(firstTable);
            console.log('📊 Selected table:', firstTable);

            console.log('📈 Fetching table statistics...');
            const statsData = await fetchTableStats(firstTable);
            console.log('📈 Stats received:', statsData);
            setStats(statsData);

            console.log('🔄 Fetching initial data...');
            await fetchData(firstTable, '', 'All Origins', 'All Countries', 0, false);

            console.log('✅ Dashboard initialized successfully');
        } catch (err) {
            console.error('❌ Dashboard initialization failed:', err);
            setError(`Initialization failed: ${err.message}`);
            setConnectionStatus('disconnected');
        } finally {
            setLoading(false);
        }
    }, [fetchData]);

    // Handle search change with debouncing
    const handleSearchChange = useCallback((newSearchTerm) => {
        console.log('🔍 Search changed:', newSearchTerm);
        setSearchTerm(newSearchTerm);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            fetchData(selectedTable, newSearchTerm, originFilter, countryFilter, 0, false);
        }, 300);
    }, [selectedTable, originFilter, countryFilter, fetchData]);

    useEffect(() => () => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (fetchAbortRef.current) fetchAbortRef.current.abort();
    }, []);

    // Handle origin filter change
    const handleOriginFilterChange = useCallback((newOriginFilter) => {
        console.log('🎯 Origin filter changed:', newOriginFilter);
        setOriginFilter(newOriginFilter);
        fetchData(selectedTable, searchTerm, newOriginFilter, countryFilter, 0, false);
    }, [selectedTable, searchTerm, countryFilter, fetchData]);

    // Handle country filter change
    const handleCountryFilterChange = useCallback((newCountryFilter) => {
        console.log('🌍 Country filter changed:', newCountryFilter);
        setCountryFilter(newCountryFilter);
        fetchData(selectedTable, searchTerm, originFilter, newCountryFilter, 0, false);
    }, [selectedTable, searchTerm, originFilter, fetchData]);

    // Handle table change
    const handleTableChange = useCallback((newTable) => {
        console.log('📊 Table changed:', newTable);
        setSelectedTable(newTable);
        // Reset filters when changing tables
        setSearchTerm('');
        setOriginFilter('All Origins');
        setCountryFilter('All Countries');

            fetchTableStats(newTable)
                .then(statsData => {
                console.log('📈 Stats updated for table change:', statsData);
                setStats(statsData);
            })
            .catch(statsError => {
                console.error('❌ Error fetching stats on table change:', statsError);
            });

        fetchData(newTable, '', 'All Origins', 'All Countries', 0, false);
    }, [fetchData]);

    // Handle refresh
    const handleRefresh = useCallback(() => {
        console.log('🔄 Refreshing data...');
        // Also refresh stats
        fetchTableStats(selectedTable)
            .then(statsData => {
                console.log('📈 Stats refreshed:', statsData);
                setStats(statsData);
            })
            .catch(err => console.error('❌ Error refreshing stats:', err));

        fetchData(selectedTable, searchTerm, originFilter, countryFilter, 0, false);
    }, [selectedTable, searchTerm, originFilter, countryFilter, fetchData]);

    // Reset all filters
    const resetFilters = useCallback(() => {
        console.log('🗑️ Resetting all filters...');
        setSearchTerm('');
        setOriginFilter('All Origins');
        setCountryFilter('All Countries');
        fetchData(selectedTable, '', 'All Origins', 'All Countries', 0, false);
    }, [selectedTable, fetchData]);

    // Load more data
    const loadMoreData = useCallback(() => {
        if (pagination.hasMore && !loading) {
            console.log('📄 Loading more data...');
            fetchData(selectedTable, searchTerm, originFilter, countryFilter, pagination.currentOffset, true);
        }
    }, [selectedTable, searchTerm, originFilter, countryFilter, pagination.currentOffset, pagination.hasMore, loading, fetchData]);

    // Update filter options when table changes
    useEffect(() => {
        if (selectedTable && connectionStatus === 'connected') {
            console.log('🔄 Updating filter options for table:', selectedTable);

            fetchFilterOptions(selectedTable)
                .then(result => {
                    // Keep file-based countries but update origins from database
                    setOrigins(result.origins);
                    console.log('✅ Filter options updated:', {
                        origins: result.origins.length,
                        destinations: result.destinations.length
                    });
                })
                .catch(err => {
                    console.error('❌ Error updating filter options:', err);
                });
        }
    }, [selectedTable, connectionStatus]);

    // Debug logging for stats changes
    useEffect(() => {
        console.log('📊 Stats state updated:', stats);
    }, [stats]);

    return {
        // Data
        accessData,
        loading,
        error,

        // Connection
        connectionStatus,

        // Filters
        searchTerm,
        originFilter,
        countryFilter,

        // Tables and options
        tables,
        selectedTable,
        countries,
        origins,
        stats,
        pagination,

        // Actions
        initializeDashboard,
        handleSearchChange,
        handleOriginFilterChange,
        handleCountryFilterChange,
        setSelectedTable: handleTableChange,
        handleRefresh,
        resetFilters,
        loadMoreData
    };
};
